import type { SupabaseClient } from '@supabase/supabase-js';
import { KF_ICON, KF_LOGO_GOECOMMERCE, KF_LOGO_KAUFLAND } from '@vario-connect/frontend-design';
import { sendVarioMessage } from '../vario-iframe.js';
import { ensureKfDialogShell, kfEscapeHtml, showKfDialogResult, KF_DLG_ICON } from './dialog-shell.js';

interface InstallContext {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  handshake: { appToken: string; appIdentifier?: string; offlineToken?: string };
  supabaseUrl: string;
}

interface InstallResponse {
  ok?: boolean;
  marketplaceConnectionId?: string;
  isNew?: boolean;
  error?: { code: string; message: string; details?: unknown };
}

/**
 * Installations-Dialog (/#/install) im go-eCommerce-/kf-page-Design — identischer Rahmen wie
 * die übrigen Kaufland-Fenster (App-Header mit go-eCommerce- + Kaufland-Logo, schwebende Karte,
 * Vario-grüner Akzent). Höhe: Auto-Grow über `ensureKfDialogShell` + `initVarioIframeHeight('auto')`
 * in main.ts — der Dialog ist kein Vollflächen-Slot, `useFullHeight` würde Varios eigenen
 * Dialog-Button weit unter die Karte schieben.
 */
export async function renderInstall(ctx: InstallContext) {
  ensureKfDialogShell();
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = `
    <div class="kf-page kf-dialog">
      <main class="main">
        <header class="apphead">
          <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
          <span class="kf-logo" title="Kaufland.de">${KF_LOGO_KAUFLAND}</span>
          <span class="hdivider"></span>
          <div class="htitle">
            <div class="heyebrow">Kaufland · Installation</div>
            <h1>Kaufland-App installieren</h1>
          </div>
          <div class="hactions">
            <span class="hstatus pending" id="kl-inst-pill"><span class="d"></span>Noch nicht verbunden</span>
          </div>
        </header>

        <div class="card" id="kl-inst-card">
          <form id="install-form" novalidate>
            <p class="dlg-intro">Hinterlege deine <b>Kaufland Seller-API-Zugangsdaten</b>. Sie werden vor dem Speichern
              direkt gegen Kaufland geprüft. Welche Marktplätze (DE, CZ, SK …) du nutzt, wählst du anschließend
              im Reiter <b>Einstellungen</b>.</p>

            <div class="sec-lab">Kaufland Seller API</div>

            <div class="dlg-fields">
              <div class="dlg-field">
                <label for="client-key">Client Key</label>
                <input type="text" id="client-key" name="clientKey" autocomplete="off" spellcheck="false" required />
              </div>
              <div class="dlg-field">
                <label for="secret-key">Secret Key</label>
                <input type="password" id="secret-key" name="secretKey" autocomplete="off" spellcheck="false" required />
              </div>
            </div>

            <p class="dlg-hint">Beide Schlüssel findest du im
              <a href="https://sellerportal.kaufland.de/settings/api" target="_blank" rel="noopener">Kaufland Seller Portal · Einstellungen → API</a>.
              Der Secret Key wird verschlüsselt gespeichert und nie wieder angezeigt.</p>

            <div class="dlg-msg" id="install-status" aria-live="polite"></div>

            <div class="dlg-foot">
              <button type="button" class="btn btn-ghost" id="cancel-btn">${KF_ICON.close}Abbrechen</button>
              <button type="submit" class="btn btn-primary" id="submit-btn" disabled>${KF_DLG_ICON.check}App installieren</button>
            </div>
          </form>
        </div>
      </main>
    </div>
  `;

  const card = ctx.contentEl.querySelector<HTMLDivElement>('#kl-inst-card')!;
  const pill = ctx.contentEl.querySelector<HTMLSpanElement>('#kl-inst-pill')!;
  const form = ctx.contentEl.querySelector<HTMLFormElement>('#install-form')!;
  const statusEl = ctx.contentEl.querySelector<HTMLDivElement>('#install-status')!;
  const submitBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#submit-btn')!;
  const cancelBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#cancel-btn')!;
  const clientInp = ctx.contentEl.querySelector<HTMLInputElement>('#client-key')!;
  const secretInp = ctx.contentEl.querySelector<HTMLInputElement>('#secret-key')!;

  let abortController: AbortController | null = null;

  function setStatus(text: string, kind: 'info' | 'ok' | 'error' = 'info') {
    const icon = kind === 'ok' ? KF_DLG_ICON.check : kind === 'error' ? KF_DLG_ICON.alert : KF_DLG_ICON.info;
    statusEl.className = `dlg-msg show ${kind}`;
    statusEl.innerHTML = `${icon}<span>${kfEscapeHtml(text)}</span>`;
  }

  function setPill(text: string, kind: 'pending' | 'ok' | 'error') {
    pill.className = kind === 'ok' ? 'hstatus' : `hstatus ${kind}`;
    pill.innerHTML = `<span class="d"></span>${kfEscapeHtml(text)}`;
  }

  function setBusy(busy: boolean) {
    submitBtn.disabled = busy || !hasBothKeys();
    submitBtn.innerHTML = busy
      ? `<span class="dlg-spin"></span>Prüfe Zugangsdaten …`
      : `${KF_DLG_ICON.check}App installieren`;
  }

  const hasBothKeys = () => clientInp.value.trim().length > 0 && secretInp.value.trim().length > 0;
  const validate = () => {
    submitBtn.disabled = !hasBothKeys();
  };
  clientInp.addEventListener('input', validate);
  secretInp.addEventListener('input', validate);

  cancelBtn.addEventListener('click', () => {
    if (abortController) {
      abortController.abort();
      return;
    }
    // Vario bietet KEIN app-seitiges Abbrechen-Signal (bestätigt vom Vario-Support 2026-07-27):
    // `installationComplete: false` schließt den Dialog nicht. `true` blendet lediglich Varios
    // eigenen Schließen-Button ein und ist der empfohlene Weg, dem Anwender einen Ausgang zu geben.
    // Intern als Abbruch behandelt (kein Install-Call → keine Verbindung angelegt).
    setPill('Abgebrochen', 'error');
    showKfDialogResult(card, {
      kind: 'neutral',
      icon: KF_ICON.close,
      title: 'Installation abgebrochen',
      text: 'Es wurden keine Zugangsdaten gespeichert. Du kannst den Dialog jetzt über den Schließen-Button beenden.',
    });
    sendVarioMessage({ installationComplete: true });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const clientKey = clientInp.value.trim();
    const secretKey = secretInp.value.trim();

    if (!clientKey || !secretKey) return setStatus('Client Key und Secret Key sind Pflichtfelder.', 'error');

    setBusy(true);
    setStatus('Prüfe Kaufland-Zugangsdaten …', 'info');
    setPill('Prüfe Zugangsdaten …', 'pending');
    abortController = new AbortController();

    try {
      const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/kaufland-install`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${ctx.session.token}`,
        },
        body: JSON.stringify({
          clientKey,
          secretKey,
          // Persist the Vario offlineToken now — the navigation slot won't have it later.
          offlineToken: ctx.handshake.offlineToken,
        }),
        signal: abortController.signal,
      });
      const payload = (await res.json()) as InstallResponse;

      if (!res.ok) {
        setStatus(payload.error?.message ?? `HTTP ${res.status}`, 'error');
        setPill('Zugangsdaten abgelehnt', 'error');
        return;
      }

      setPill('Kaufland verbunden', 'ok');
      showKfDialogResult(card, {
        kind: 'ok',
        icon: KF_DLG_ICON.checkBold,
        title: 'Kaufland-App installiert',
        text: 'Die Zugangsdaten sind geprüft und verschlüsselt gespeichert. Aktiviere jetzt deine Marktplätze im Reiter „Einstellungen".',
        note: 'Kaufland Seller API · verbunden',
      });

      sendVarioMessage({ installationComplete: true });
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        setStatus('Installation abgebrochen.', 'info');
        setPill('Noch nicht verbunden', 'pending');
      } else {
        setStatus(`Fehler: ${(err as Error).message}`, 'error');
        setPill('Verbindung fehlgeschlagen', 'error');
      }
    } finally {
      abortController = null;
      setBusy(false);
    }
  });
}
